import React from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import Icon from "../components/Icon/Icon";
import PreLoader from "../components/PreLoader/PreLoader";
import Tooltip from "../components/Tooltip/Tooltip";
import { ParcelCol } from "./parcelsStyles";
import { valueLabel } from "./typography";

export const OutgoingCol = styled(ParcelCol)`
  &:first-child {
    word-break: normal;
  }

  ${(props) => props.theme.xs`
    &:nth-child(3), &:nth-child(5) {
      justify-content: flex-start;
    }

    &:nth-child(3) {
      order: 1;
    }

    &:first-child {
      order: -3;
      word-break: normal;
    }

    &:nth-child(2) {
      order: -2;
    }

    &:last-child {
      order: -1;
      text-align: end;
    }
  `}
`;

/*
 * Desktop row cells (flex variant of the Storage list pattern, see
 * containers/Outgoing/components/rowLayout.ts). Spare width is spread
 * across the cells by flex-grow ratio — carrier 3x, others 1x (dimensions
 * pass 1.5x), sku anchored at 0 — with the px constants as flex-bases the
 * cells never shrink below; the layout tier drops cells instead of letting
 * them wrap. Mobile rows keep the react-grid-system OutgoingCol markup
 * above.
 */
export const OutgoingMainCol = styled(ParcelCol)`
  padding-left: 0 !important;
  white-space: nowrap;
  word-break: normal;
`;

const cellBase = css`
  display: flex;
  align-items: center;
  padding: 0 10px;
  line-height: 20px;

  & > span {
    margin: 0 2px;
    color: ${(props) => props.theme.textSubtle};
  }
`;

export const OutgoingGrowCol = styled.div`
  ${cellBase};
  flex: 3 1 0%;
  min-width: 0;
`;

export const OutgoingGrowText = styled.span`
  && {
    margin: 0;
    color: inherit;
  }
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OutgoingMetaCol = styled.div<{ $width?: number; $grow?: number }>`
  ${cellBase};
  flex: ${({ $grow }) => $grow ?? 1} 0
    ${({ $width }) => ($width ? `${$width}px` : "auto")};
  white-space: nowrap;
`;

export const OutgoingEndCol = styled(OutgoingMetaCol)`
  justify-content: flex-end;
`;

export const OutgoingActionsCol = styled.div`
  ${cellBase};
  flex: 0 1 auto;
  min-width: 0;
  margin-left: auto;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 6px;
  white-space: normal;
`;

export const SectionHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const Spinner = styled(PreLoader)`
  position: absolute;
  top: 100px;
`;

export const HelpIcon = styled(Icon)`
  width: 12px;
  height: 12px;
`;

export const HeaderTitle = styled.p`
  ${valueLabel}
  display: flex;
  align-items: center;
  margin: 0;
  color: ${(props) => props.theme.textMuted};

  i {
    width: 18px;
    height: 18px;
    margin-right: 6px;
  }
`;

export const Section = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  padding-top: 15px;

  &:first-child {
    padding-top: 0;
  }

  ${HeaderTitle} {
    font-weight: 700;
  }
`;

export const InnerSection = styled(Section)`
  padding: 10px 0 5px 24px;

  ${SectionHeader},
  ${HeaderTitle} {
    font-weight: 700;
  }
`;

export const SectionContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding-top: 10px;

  &:first-child {
    padding-top: 0;
  }

  img {
    margin-left: 8px;
  }
`;

export const ButtonSection = styled.div`
  position: absolute;
  justify-content: space-evenly;
  display: flex;
  align-items: center;
  left: 0;
  right: 0;
  bottom: 100%;
  background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.fadeMaskTransparent} 0%,
      ${({ theme }) => theme.surface} 100%
    )
    no-repeat;
`;

export const TotalPrice = styled.p`
  margin: 12px 0;
  color: ${(props) => props.theme.black50};

  span {
    color: ${(props) => props.theme.black};
    font-size: 16px;
    font-weight: 700;
    margin-left: 4px;
  }

  i {
    display: inline-block;
    width: 13px;
    height: 13px;
    margin-left: 4px;
  }
`;

export const TimerSection = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.booksBg};

  i {
    width: 30px;
    height: 30px;
    margin-right: 12px;
  }
`;

export const TextWrapper = styled.div`
  color: ${(props) => props.theme.black50};
  font-size: 12px;

  p {
    margin: 6px 0;

    span {
      font-weight: 700;
    }
  }
`;

export const Warning = styled.div`
  background-color: ${({ theme }) => theme.errorFocusRing};
  color: ${({ theme }) => theme.textSubtle};
  padding: 12px;
  font-size: 12px;
  line-height: 18px;
`;

export const ExcessMarker = () => {
  const { t } = useTranslation("common");

  return (
    <Tooltip
      body={t("tooltips.excessMarker")}
      place={"bottom"}
      openEvents={{
        click: isMobile,
        mouseover: !isMobile,
        focus: !isMobile,
      }}
    >
      <ExcessMarkerWrapper>
        <ExcessMarkerIcon>*</ExcessMarkerIcon>
      </ExcessMarkerWrapper>
    </Tooltip>
  );
};

const ExcessMarkerWrapper = styled.div`
  min-width: 24px;
  width: 24px;
  height: 24px;
  margin-right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExcessMarkerIcon = styled.div`
  ::after {
    content: "*";
  }
  margin-left: 2px;
  font-size: 16px;
  font-weight: bold;
  color: ${(props) => props.theme.red};
`;
